import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Briefcase, Gear, ChatCircleDots, ShareNetwork, Factory, ClipboardText, Kanban, BookOpen, Barcode, Storefront, Sparkle, Wrench, Armchair, UserCircle, Files } from '@phosphor-icons/react';
import { useAuth, canSeeSettings, canUseApp } from '@/auth/AuthContext';
import { useUnverifiedCount } from '@/hooks/useUnverifiedCount';
import { usePlannerAlerts } from '@/hooks/usePlannerAlerts';
import { useT } from '@/i18n/LanguageContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import { toast } from 'sonner';

import DesktopNav from '@/components/layout/DesktopNav';
import LanguageToggle from '@/components/layout/LanguageToggle';
import MobileNav from '@/components/layout/MobileNav';
import Breadcrumb from '@/components/layout/Breadcrumb';
import AccountMenu from '@/components/layout/AccountMenu';
import BottomTabBar from '@/components/layout/BottomTabBar';
import TaskTray from '@/components/layout/TaskTray';
import BuildTag from '@/components/BuildTag';
import { LanDirectBadge } from '@/components/LanDirectBadge';

// Per-app header context — drives the dynamic "current app" chip so the user
// always knows which workspace they're in (勤怠管理 / チャット / IT / 設定).
const APP_CONTEXTS = {
  it:       { id: 'it',       Icon: ShareNetwork,   gradient: 'from-violet-500 to-indigo-600' },
  mfg:      { id: 'mfg',      Icon: Factory,        gradient: 'from-orange-500 to-amber-600' },
  shizai:   { id: 'shizai',   Icon: ClipboardText,  gradient: 'from-rose-500 to-pink-600' },
  marketing:{ id: 'marketing', Icon: Storefront,    gradient: 'from-fuchsia-600 to-violet-600' },
  // iter_143h · parts_request is its OWN app (own path, own capability, own
  // RBAC gate at RequireApp app="parts_request"). Previously the header
  // showed "資材" because of a URL-prefix fallthrough — this misled users
  // ("is 部品受注入力 under shizai?"). Give it a distinct chip that matches
  // the PartsRequest.js in-page header (orange→rose clipboard).
  parts_request: { id: 'parts_request', Icon: ClipboardText, gradient: 'from-orange-500 to-rose-600' },
  planner:  { id: 'planner',  Icon: Kanban,         gradient: 'from-indigo-500 to-blue-600' },
  engineering: { id: 'engineering', Icon: Wrench,   gradient: 'from-teal-500 to-cyan-600' },
  bihin:    { id: 'bihin',    Icon: Armchair,       gradient: 'from-green-600 to-emerald-700' },
  chat:     { id: 'chat',     Icon: ChatCircleDots, gradient: 'from-emerald-500 to-teal-600' },
  settings: { id: 'settings', Icon: Gear,           gradient: 'from-zinc-500 to-zinc-700' },
  knowledge:{ id: 'knowledge', Icon: BookOpen,      gradient: 'from-sky-500 to-blue-600' },
  scanner:  { id: 'scanner',  Icon: Barcode,        gradient: 'from-cyan-500 to-teal-600' },
  docs:     { id: 'docs',     Icon: Files,          gradient: 'from-teal-600 to-emerald-700' },
  myit:     { id: 'myit',     Icon: UserCircle,     gradient: 'from-blue-500 to-sky-600' },
  demo:     { id: 'demo',     Icon: Sparkle,        gradient: 'from-fuchsia-500 to-violet-600' },
  kintai:   { id: 'kintai',   Icon: Briefcase,      gradient: 'from-[#0052FF] to-indigo-600' },
};
function appContextFor(pathname) {
  if (pathname.startsWith('/my-it')) return APP_CONTEXTS.myit;
  if (pathname.startsWith('/it')) return APP_CONTEXTS.it;
  if (pathname.startsWith('/mfg')) return APP_CONTEXTS.mfg;
  // Check parts-request BEFORE shizai — it's a separate app with its own chip.
  if (pathname.startsWith('/parts-request')) return APP_CONTEXTS.parts_request;
  if (pathname.startsWith('/shizai')) return APP_CONTEXTS.shizai;
  if (pathname.startsWith('/marketing')) return APP_CONTEXTS.marketing;
  if (pathname.startsWith('/planner')) return APP_CONTEXTS.planner;
  if (pathname.startsWith('/engineering')) return APP_CONTEXTS.engineering;
  if (pathname.startsWith('/bihin')) return APP_CONTEXTS.bihin;
  if (pathname.startsWith('/chat')) return APP_CONTEXTS.chat;
  if (pathname.startsWith('/settings')) return APP_CONTEXTS.settings;
  if (pathname.startsWith('/knowledge')) return APP_CONTEXTS.knowledge;
  if (pathname.startsWith('/scanner')) return APP_CONTEXTS.scanner;
  if (pathname.startsWith('/documents')) return APP_CONTEXTS.docs;
  if (pathname.startsWith('/demo')) return APP_CONTEXTS.demo;
  return APP_CONTEXTS.kintai;
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useT();
  const showSettings = canSeeSettings(user) && canUseApp(user, 'settings');
  const unverifiedCount = useUnverifiedCount(user);
  const plannerAlerts = usePlannerAlerts(user);
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success(t('logout.success'));
    navigate('/login', { replace: true });
  };

  const appCtx = appContextFor(location.pathname);
  const ChipIcon = appCtx.Icon;
  // Scanner routes get a compact layout: no bottom padding reservation (tab
  // bar is hidden) and tighter top padding so the scanner viewport is closer
  // to the header on mobile.
  const isScanner = location.pathname === '/scanner' || location.pathname.startsWith('/scanner/');
  // Documents is a list-first workspace: on large monitors the 1280px shell left
  // most of the screen unused, so the document list gets a wider container.
  const isDocs = location.pathname === '/documents' || location.pathname.startsWith('/documents/');
  const shell = isDocs ? 'max-w-[1720px]' : 'max-w-7xl';
  const mainCls = isScanner
    ? `${shell} mx-auto px-3 sm:px-6 pt-3 sm:pt-8 pb-6`
    : `${shell} mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-24 md:pb-8`;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header
        className={`bg-white/95 supports-[backdrop-filter]:bg-white/90 sticky top-0 z-50 no-print transition-shadow ${
          scrolled
            ? 'shadow-[0_1px_0_rgba(0,0,0,0.04),0_6px_20px_-12px_rgba(0,0,0,0.12)] border-b border-transparent'
            : 'border-b border-zinc-200/70'
        }`}
      >
        <div className={`${shell} mx-auto px-4 sm:px-6`}>
          <div className="h-14 sm:h-16 flex items-center gap-2 sm:gap-3">
            {/* Left: logo + current-app chip */}
            <button
              type="button"
              onClick={() => navigate('/')}
              data-testid="nav-home"
              className="group flex items-center gap-2 sm:gap-2.5 rounded-xl hover:bg-zinc-100/70 pl-1 pr-1.5 sm:pr-3 py-1.5 -ml-1 transition-colors min-w-0"
              aria-label={t('common.home')}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_shift-directive-app/artifacts/62hq2mxj_sensbey%20logo.png"
                alt="Sensbey"
                className="hidden sm:block h-5 w-auto shrink-0"
              />
              <span className="inline-flex items-center gap-2 sm:pl-2.5 sm:border-l sm:border-zinc-200 min-w-0">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br ${appCtx.gradient} text-white shadow-sm shrink-0 transition-transform group-hover:scale-105`}>
                  <ChipIcon size={15} weight="fill" />
                </span>
                <span className="text-sm font-semibold tracking-tight text-zinc-900 truncate" data-testid="header-app-name">
                  {t(`app.${appCtx.id}`)}
                </span>
              </span>
            </button>

            {/* Left-aligned context nav (申請フォーム / 申請履歴 — kintai only, desktop) */}
            <DesktopNav unverifiedCount={unverifiedCount} />

            {/* Spacer pushes the account cluster to the right edge */}
            <div className="flex-1" />

            {/* Right cluster: language toggle + account pill + mobile toggle */}
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <LanDirectBadge />
              <TaskTray />
              <LanguageToggle className="hidden md:inline-flex" />
              <AccountMenu
                user={user}
                open={accountOpen}
                onOpenChange={setAccountOpen}
                onLogout={handleLogout}
                plannerAlerts={plannerAlerts}
              />
              <MobileNav
                user={user}
                showSettings={showSettings}
                unverifiedCount={unverifiedCount}
                open={mobileOpen}
                onOpenChange={setMobileOpen}
                onLogout={handleLogout}
              />
            </div>
          </div>
          <Breadcrumb />
        </div>
      </header>
      <main className={mainCls}>
        {/* 1画面の例外でアプリ全体が真っ白にならないようにする
            （2026-10-03 本番: /it/assets が ReferenceError で白画面になった）。
            画面を移動したら key が変わって自動で復帰する。 */}
        <ErrorBoundary key={location.pathname}
          title="この画面を開くときに問題が発生しました。"
          message="もう一度お試しいただくか、ホームへお戻りください。"
          backTo="/" backLabel="ホームに戻る">
          <Outlet />
        </ErrorBoundary>
      </main>
      <BottomTabBar user={user} />
      <BuildTag />
    </div>
  );
}

